import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

/**
 * Reusable image uploader with preview.
 * Props:
 *  - value: string URL (current image)
 *  - onChange: fn(newUrl) called after upload or when cleared
 *  - recommended: text label e.g. "Recommended: 1920 × 1080"
 *  - aspect: css aspect e.g. "16/9" or "4/5"
 *  - testId
 */
export default function ImageUploader({ value, onChange, recommended = "", aspect = "16/9", testId }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const upload = async (file) => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(process.env.REACT_APP_BACKEND_URL + data.url);
            toast.success("Uploaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };

    return (
        <div className="space-y-2" data-testid={testId}>
            {recommended && <div className="font-mono text-[10px] uppercase text-neutral-500">{recommended}</div>}
            {value ? (
                <div className="relative border-2 border-black bg-neutral-50" style={{ aspectRatio: aspect }}>
                    <img src={value} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 flex gap-1">
                        <button type="button" onClick={() => ref.current?.click()} className="bg-white border-2 border-black px-2 py-1 font-mono text-[10px] uppercase">Replace</button>
                        <button type="button" onClick={() => onChange("")} className="bg-white border-2 border-black px-2 py-1 font-mono text-[10px] uppercase" title="Remove"><X className="w-3 h-3" /></button>
                    </div>
                </div>
            ) : (
                <button type="button" onClick={() => ref.current?.click()} className="w-full border-2 border-dashed border-black bg-neutral-50 flex flex-col items-center justify-center gap-2 py-10 hover:bg-neutral-100" style={{ aspectRatio: aspect }}>
                    <Upload className="w-6 h-6" />
                    <div className="font-mono text-xs uppercase">{busy ? "Uploading…" : "Click to upload"}</div>
                </button>
            )}
            <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); }} />
        </div>
    );
}
